import React, { useState } from 'react';
import { Button, Modal, Text, BlockStack, InlineStack, Badge, TextField, Tooltip } from '@shopify/polaris';
import { RefreshMajor } from '@shopify/polaris-icons';
import { 
  ORDER_STATUS, 
  MANUAL_ACTION_TYPE, 
  MANUAL_ACTION_AVAILABILITY,
  MANUAL_ACTION_PERMISSIONS 
} from '../../constants/orderConstants';
import { authService } from '../../services/authService';

const ManualTrialRegenerationButton = ({ 
  order,
  orderItem, 
  onTrialRegenerated, 
  userPermissions = [],
  disabled = false,
  size = 'medium',
  variant = 'secondary'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [reason, setReason] = useState('');

  // Extract trial ID from order item properties or use item ID
  const trialId = orderItem?.trial_id || orderItem?.id;

  // Check if user has permission to regenerate trials
  
  // Check if order status allows trial regeneration
  const isStatusAllowed = MANUAL_ACTION_AVAILABILITY[MANUAL_ACTION_TYPE.TRIAL_REGENERATE].includes(order?.status);
  
  // Check if trial ID is available
  const hasTrialId = trialId && trialId.toString().trim() !== '';

  // Determine if button should be shown and enabled
  const shouldShow = isStatusAllowed && hasTrialId;
  const isButtonDisabled = disabled || isRegenerating;

  const handleRegenerateTrial = async () => {
    if (!trialId || !order?.order_id || !orderItem?.id) return;

    setIsRegenerating(true);
    try {
      // Call the API to regenerate trial for specific order item
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/trials/${order?.order_id}/items/${orderItem?.id}/regenerate-trial`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: reason.trim() || 'Manual regeneration requested',
          userId: localStorage.getItem('userId') || 'unknown'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Success callback
        if (onTrialRegenerated) {
          onTrialRegenerated({
            trialId: trialId,
            orderId: order?.id,
            orderItemId: orderItem?.id,
            status: result.data.status,
            regeneratedAt: result.data.regeneratedAt,
            regeneratedBy: result.data.regeneratedBy,
            reason: reason.trim() || 'Manual regeneration requested'
          });
        }
        
        setIsModalOpen(false);
        setReason(''); // Reset reason field
      } else {
        throw new Error(result.message || 'Failed to regenerate trial');
      }
    } catch (error) {
      console.error('Error regenerating trial:', error);
      // You might want to show a toast notification here
      alert(`Error regenerating trial: ${error.message}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setReason(''); // Reset reason when closing
  };

  if (!shouldShow) {
    return null;
  }

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <Tooltip content={isRegenerating ? 'Regenerating trial...' : 'Regenerate Trial'}>
        <Button
          variant="tertiary"
          size="slim"
          loading={isRegenerating}
          disabled={isButtonDisabled}
          onClick={() => setIsModalOpen(true)}
          icon={RefreshMajor}
        />
      </Tooltip>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title={
          <div style={{ padding: '16px' }}>
            <Text variant="headingMd" as="h2">
              Regenerate Trial
            </Text>
          </div>
        }
        primaryAction={{
          content: 'Regenerate Trial',
          onAction: handleRegenerateTrial,
          loading: isRegenerating,
          variant: 'primary'
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModalClose
          }
        ]}
        footer={<div style={{ padding: '16px' }} />}
      >
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd">
            Are you sure you want to regenerate the trial for this order item? This will trigger a new generation process.
          </Text>
          
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Order Item Details:
            </Text>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Order ID:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {order?.order_id}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Product:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {orderItem?.name || 'N/A'}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Item ID:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {orderItem?.sku || orderItem?.id}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Trial ID:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {trialId}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Customer:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {order?.customer?.name}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Order Status:</Text>
              <Badge tone="info">
                {order?.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </InlineStack>
          </BlockStack>

          <TextField
            label="Reason for regeneration (optional)"
            value={reason}
            onChange={setReason}
            placeholder="e.g., Quality issues, customer request, technical problems..."
            multiline={3}
            autoComplete="off"
            helpText="Provide a reason for regenerating this trial. This will be logged for tracking purposes."
          />

          <Text as="p" variant="bodyMd" tone="subdued">
            This will update the trial status to "Regenerating" for this specific item and trigger the ComfyUI workflow. 
            The process may take several minutes to complete.
          </Text>
        </BlockStack>
      </Modal>
    </>
  );
};

export default ManualTrialRegenerationButton;